import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useMemo, useState } from 'react'
import { AssignSheet } from '../components/AssignSheet'
import { EquipmentSlot } from '../components/EquipmentSlot'
import { NeedBar } from '../components/NeedBar'
import { SkillBar } from '../components/SkillBar'
import { StatusBadge } from '../components/StatusBadge'
import { effectiveWeaponDamage } from '../game/combat'
import { getItemDef, ITEMS } from '../game/items'
import { useI18n } from '../i18n/useI18n'
import {
  BOOK_SKILL_MAP,
  CORE_SKILLS,
  getSkillLevel,
  isSkillUnlocked,
  SURVIVOR_PROGRESS_SKILL,
  xpForSkillLevel,
} from '../game/skills'
import type { SkillKey, SurvivorRole } from '../game/types'
import { getPortraitSrc } from '../game/portraits'
import { useGameStore } from '../store/useGameStore'

const ROLE_KEYS: Record<
  SurvivorRole,
  'role_scavenger' | 'role_medic' | 'role_cook' | 'role_trader'
> = {
  Scavenger: 'role_scavenger',
  Medic: 'role_medic',
  Cook: 'role_cook',
  Trader: 'role_trader',
}

type Props = {
  survivorId: string
  onBack: () => void
}

export function SurvivorDetailScreen({ survivorId, onBack }: Props) {
  const { t, pick } = useI18n()
  const survivor = useGameStore((s) =>
    s.survivors.find((x) => x.id === survivorId),
  )
  const inventory = useGameStore((s) => s.inventory)
  const learnSkillFromBook = useGameStore((s) => s.learnSkillFromBook)
  const equipWeaponFromStash = useGameStore((s) => s.equipWeaponFromStash)
  const unequipWeaponToStash = useGameStore((s) => s.unequipWeaponToStash)
  const [assigning, setAssigning] = useState(false)
  const [weaponSheetOpen, setWeaponSheetOpen] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const stashWeapons = useMemo(
    () => inventory.filter((stack) => ITEMS[stack.defId]?.category === 'weapon'),
    [inventory],
  )

  const equippedWeapon = survivor?.loadout.weapon
  const weaponSubtitle = useMemo(() => {
    if (!survivor) return undefined
    const ew = survivor.loadout.weapon
    if (ew?.weaponFamily == null || ew.baseDamage == null) return undefined
    return t('detail_weapon_effective', {
      effective: effectiveWeaponDamage(survivor, ew.baseDamage, ew.weaponFamily),
      base: ew.baseDamage,
    })
  }, [survivor, t])

  useEffect(() => {
    if (!weaponSheetOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setWeaponSheetOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [weaponSheetOpen])

  if (!survivor) {
    return (
      <div className="space-y-3">
        <BackBar onBack={onBack} label={t('detail_back')} />
        <p className="rounded-md border border-stone-800 bg-stone-800 p-3 text-sm text-stone-400">
          {t('detail_not_found')}
        </p>
      </div>
    )
  }

  const initials = survivor.name.slice(0, 2).toUpperCase()
  const portraitSrc = getPortraitSrc(survivor.portrait)
  const isIdle = survivor.status === 'Idle'
  const bioText = pick(survivor.bio).trim()
  const progression = survivor.skills[SURVIVOR_PROGRESS_SKILL]
  const survivorLevel = progression?.level ?? 1
  const survivorXp = progression?.xp ?? 0
  const survivorXpRequired = xpForSkillLevel(survivorLevel)
  const xpPercent =
    survivorLevel >= 10 ? 100 : Math.min(100, (survivorXp / survivorXpRequired) * 100)
  const injuryStatusLabel =
    survivor.status === 'Injured'
      ? `${t('status_injured')} ${formatMmSs(Math.max(0, (survivor.injuryEndsAt ?? now) - now))}`
      : undefined

  const availableBooks = inventory.filter((stack) => BOOK_SKILL_MAP[stack.defId])
  const unlockedExtraSkills = (Object.keys(survivor.skills) as SkillKey[]).filter(
    (id) =>
      id !== SURVIVOR_PROGRESS_SKILL &&
      !CORE_SKILLS.includes(id) &&
      isSkillUnlocked(survivor, id),
  )

  return (
    <div className="space-y-4">
      <BackBar onBack={onBack} label={t('detail_back')} />

      <header className="flex items-center gap-3">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-stone-700 bg-linear-to-br from-stone-800 to-stone-950">
          {portraitSrc ? (
            <img src={portraitSrc} alt={survivor.name} className="h-full w-full object-cover" />
          ) : (
            <div className="grid h-full w-full place-items-center font-mono text-xl font-bold text-stone-300">
              {initials}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h1 className="truncate text-lg font-bold text-stone-100">
              {survivor.name}
            </h1>
            <StatusBadge status={survivor.status} labelOverride={injuryStatusLabel} />
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500">
            <span>{t(ROLE_KEYS[survivor.role])}</span>
            <span aria-hidden>•</span>
            <span>{t('detail_survivor_level', { level: survivorLevel })}</span>
          </div>
          <div className="mt-1.5">
            <div className="h-1.5 overflow-hidden rounded-sm bg-stone-800">
              <div
                className="h-full bg-amber-500 transition-[width] duration-500"
                style={{ width: `${xpPercent}%` }}
              />
            </div>
            <div className="mt-1 text-[10px] text-stone-500">
              {survivorLevel >= 10
                ? t('detail_survivor_max_level')
                : t('detail_survivor_xp', {
                    current: survivorXp,
                    required: survivorXpRequired,
                  })}
            </div>
          </div>
        </div>
      </header>

      {bioText && (
        <p className="rounded-md border border-stone-800 bg-stone-800/50 p-3 text-xs italic leading-relaxed text-stone-400">
          {bioText}
        </p>
      )}

    <Section title={t('detail_needs')}>
        <div className="space-y-2">
          <NeedBar label={t('need_hunger')} value={survivor.needs.hunger} />
          <NeedBar label={t('need_thirst')} value={survivor.needs.thirst} />
          <NeedBar label={t('need_energy')} value={survivor.needs.energy} />
        </div>
      </Section>

      <Section title={t('detail_skills')}>
        <div className="space-y-2">
          {CORE_SKILLS.map((key) => (
            <div key={key}>
              <SkillBar label={t(`skill_${key}` as never)} value={getSkillLevel(survivor, key)} max={10} />
              <div className="ml-26 pl-2 text-[10px] italic text-stone-500">
                {t(`skill_note_${key}` as never)}
              </div>
            </div>
          ))}
          {unlockedExtraSkills.length > 0 && (
            <div className="mt-3 space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-stone-500">
                {t('detail_extra_skills')}
              </div>
              {unlockedExtraSkills.map((key) => (
                <SkillBar
                  key={key}
                  label={t(`skill_${key}` as never)}
                  value={getSkillLevel(survivor, key)}
                  max={10}
                />
              ))}
            </div>
          )}
        </div>
      </Section>

      <Section title={t('detail_skill_books')}>
        {availableBooks.length === 0 ? (
          <p className="text-xs text-stone-500">{t('detail_no_skill_books')}</p>
        ) : (
          <div className="space-y-2">
            {availableBooks.map((stack) => {
              const targetSkill = BOOK_SKILL_MAP[stack.defId]
              const alreadyUnlocked = isSkillUnlocked(survivor, targetSkill)
              return (
                <button
                  key={stack.defId}
                  type="button"
                  disabled={alreadyUnlocked}
                  onClick={() => learnSkillFromBook(survivor.id, stack.defId)}
                  className="w-full rounded-md border border-stone-800 bg-stone-800/60 px-3 py-2 text-left disabled:opacity-50"
                >
                  <div className="text-sm font-medium text-stone-200">
                    {t('detail_learn_skill', { skill: t(`skill_${targetSkill}` as never) })}
                  </div>
                  <div className="text-[10px] text-stone-500">
                    {alreadyUnlocked ? t('detail_skill_already_unlocked') : t('detail_consume_book')}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </Section>

      <Section title={t('detail_loadout')}>
        <div className="space-y-2">
          <EquipmentSlot
            slot="weapon"
            equipment={survivor.loadout.weapon ?? null}
            subtitle={weaponSubtitle}
            trailing={
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setWeaponSheetOpen(true)}
                  className="rounded-md border border-amber-800/50 bg-amber-950/40 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-200 hover:border-amber-700"
                >
                  {equippedWeapon?.weaponDefId ? t('detail_weapon_swap') : t('detail_weapon_equip')}
                </button>
                {equippedWeapon?.weaponDefId ? (
                  <button
                    type="button"
                    onClick={() => unequipWeaponToStash(survivor.id)}
                    className="rounded-md border border-stone-700 bg-stone-800/80 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-300 hover:border-amber-800/50 hover:text-amber-200"
                  >
                    {t('detail_weapon_unequip')}
                  </button>
                ) : null}
              </div>
            }
          />
          <EquipmentSlot
            slot="tool"
            equipment={survivor.loadout.tool ?? null}
          />
        </div>
        <p className="mt-2 text-[10px] italic text-stone-500">
          {t('detail_loadout_note')}
        </p>
      </Section>

      <button
        type="button"
        disabled={!isIdle}
        onClick={() => setAssigning(true)}
        className="w-full rounded-md border border-amber-800/60 bg-amber-900/30 px-3 py-3 text-sm font-semibold uppercase tracking-wider text-amber-200 transition-colors hover:bg-amber-900/60 disabled:cursor-not-allowed disabled:border-stone-800 disabled:bg-stone-800 disabled:text-stone-600"
      >
        {isIdle ? t('detail_send') : t('detail_unavailable')}
      </button>

      <AssignSheet
        survivor={assigning ? survivor : null}
        onClose={() => setAssigning(false)}
      />

      <AnimatePresence>
        {weaponSheetOpen && (
          <motion.div
            key="survivor-weapon-sheet"
            className="fixed inset-0 z-50 flex items-end justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => setWeaponSheetOpen(false)}
          >
            <div className="absolute inset-0 bg-black/70" />
            <motion.div
              className="relative w-full max-w-[430px] rounded-t-2xl border-t border-stone-700 bg-stone-950 p-4 pb-6 shadow-2xl"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-stone-700" />
              <h2 className="text-base font-semibold text-stone-100">
                {t('detail_weapon_sheet_title')}
              </h2>
              <p className="mt-1 text-[11px] text-stone-500">
                {t('detail_weapon_sheet_note')}
              </p>
              {stashWeapons.length === 0 ? (
                <p className="mt-3 rounded-md border border-stone-800 bg-stone-800 p-3 text-sm text-stone-500">
                  {t('detail_weapon_sheet_empty')}
                </p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {stashWeapons.map((stack) => {
                    const def = getItemDef(stack.defId)
                    if (!def?.weapon) return null
                    const bowLocked =
                      def.weapon.family === 'bow' && !isSkillUnlocked(survivor, 'archery')
                    const currentDamage =
                      equippedWeapon?.baseDamage && equippedWeapon.weaponFamily
                        ? effectiveWeaponDamage(
                            survivor,
                            equippedWeapon.baseDamage,
                            equippedWeapon.weaponFamily,
                          )
                        : 0
                    const nextDamage = effectiveWeaponDamage(
                      survivor,
                      def.weapon.dmg,
                      def.weapon.family,
                    )
                    const delta = nextDamage - currentDamage
                    const deltaClass =
                      delta > 0
                        ? 'text-emerald-400'
                        : delta < 0
                          ? 'text-red-400'
                          : 'text-orange-400'
                    return (
                      <li key={stack.defId}>
                        <button
                          type="button"
                          disabled={bowLocked}
                          onClick={() => {
                            equipWeaponFromStash(survivor.id, stack.defId)
                            setWeaponSheetOpen(false)
                          }}
                          className="w-full rounded-md border border-stone-800 bg-stone-800 p-3 text-left transition-colors hover:border-amber-700 hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate text-sm text-stone-200">
                              {pick(def.name)}
                            </span>
                            <span className={`shrink-0 font-mono text-xs font-semibold ${deltaClass}`}>
                              {delta > 0 ? '+' : ''}
                              {delta}
                            </span>
                          </div>
                          <div className="mt-1 text-[10px] text-stone-500">
                            {t('detail_weapon_effective', {
                              effective: nextDamage,
                              base: def.weapon.dmg,
                            })}
                          </div>
                          
                          {bowLocked ? (
                            <div className="mt-1 text-[10px] italic text-red-400">
                              {t('detail_weapon_bow_locked')}
                            </div>
                          ) : null}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
              <button
                type="button"
                onClick={() => setWeaponSheetOpen(false)}
                className="mt-4 w-full rounded-md border border-stone-800 bg-stone-800 px-3 py-2 text-sm uppercase tracking-wider text-stone-400 hover:bg-stone-800"
              >
                {t('assign_cancel')}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function BackBar({ onBack, label }: { onBack: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="rounded-lg border border-stone-700 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-stone-200 transition hover:border-amber-600 hover:text-amber-300"
    >
      {label}
    </button>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
        {title}
      </h2>
      {children}
    </section>
  )
}

function formatMmSs(remainingMs: number): string {
  const totalSeconds = Math.ceil(remainingMs / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}
